import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Card variants using CVA
 *
 * Variants:
 * - default: Standard card with subtle shadow
 * - elevated: Card with stronger shadow, hover effect
 * - outlined: Border-only, no shadow
 * - metric: Gradient background for stat cards
 * - interactive: Clickable card with hover border
 */
export const cardVariants = cva(
  'rounded-xl border transition-all duration-normal',
  {
    variants: {
      variant: {
        default:
          'border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900',
        elevated:
          'border-gray-200 bg-white shadow-md hover:shadow-lg dark:border-gray-700 dark:bg-gray-900',
        outlined:
          'border-2 border-gray-300 bg-transparent dark:border-gray-600',
        metric:
          'border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-sm dark:border-gray-700 dark:from-gray-900 dark:to-gray-800',
        interactive:
          'border-gray-200 bg-white shadow-sm hover:border-primary-300 hover:shadow-md cursor-pointer dark:border-gray-700 dark:bg-gray-900 dark:hover:border-primary-600',
      },
      padding: {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
    },
  }
);

/**
 * Card Component
 */
const Card = React.forwardRef(({ className, variant, padding, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(cardVariants({ variant, padding }), className)}
    {...props}
  />
));
Card.displayName = 'Card';

/**
 * CardHeader Component
 */
const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 pb-4', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

/**
 * CardTitle Component
 */
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'font-display text-xl font-semibold leading-none tracking-tight text-gray-900 dark:text-white',
      className
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

/**
 * CardDescription Component
 */
const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-gray-600 dark:text-gray-400', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

/**
 * CardContent Component
 */
const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('', className)} {...props} />
));
CardContent.displayName = 'CardContent';

/**
 * CardFooter Component
 */
const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-4', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
export default Card;
